import React, { useState, useRef, useEffect, useCallback } from "react";
import { volumeStorage } from "../../Utils/volumeStorage";

const HEIGHT = 100;

function Volume({ defaultValue, onChange }) {
  // Initialize from localStorage (volume is inverted internally: 0 = loud, 1 = quiet)
  const [volume, setVolume] = useState(() => {
    const storedVolume = volumeStorage.get();
    // Convert stored actual volume (0 = quiet, 1 = loud) to internal inverted format
    return 1 - storedVolume;
  });
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef(null);
  // Store the previous non-zero volume for unmuting
  const previousVolumeRef = useRef(1 - volumeStorage.get());

  // Volume is inverted (0 = bottom, 1 = top)
  const volumePercentage = (1 - volume) * 100;

  const updateVolume = useCallback((newVolume) => {
    const rounded = Math.floor(newVolume * 100) / 100;
    if (rounded >= 0 && rounded <= 1) {
      setVolume(rounded);
      if (onChange) onChange(1 - rounded);
    }
  }, [onChange]);

  const updateVolumeFromPosition = useCallback((clientY) => {
    if (!trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const clickY = clientY - rect.top;
    const percentage = Math.max(0, Math.min(1, clickY / rect.height));

    updateVolume(percentage);
  }, [updateVolume]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    updateVolumeFromPosition(e.clientY);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    updateVolumeFromPosition(e.clientY);
  }, [isDragging, updateVolumeFromPosition]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, [isDragging]);

  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Finer control: 0.02 increments (2% steps)
    const delta = e.deltaY > 0 ? 0.02 : -0.02;
    updateVolume(volume + delta);
  };

  const handleIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const actualVolume = 1 - volume;

    if (actualVolume > 0) {
      // Currently not muted - mute it
      updateVolume(1); // Internal format: 1 = muted (0% actual volume)
    } else {
      // Currently muted - restore previous volume
      // Use previous volume or default to 0.5 (50%) if no previous volume
      const volumeToRestore = previousVolumeRef.current ?? 0.5;
      updateVolume(volumeToRestore);
    }
  };

  // Handle global mouse events when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Persist volume to localStorage whenever it changes (but not when muted)
  useEffect(() => {
    // Convert internal inverted volume to actual volume before storing
    const actualVolume = 1 - volume;

    // Save previous non-zero volume for unmuting
    if (actualVolume > 0) {
      previousVolumeRef.current = volume;
      // Only persist non-zero volumes (muted state is not persisted)
      volumeStorage.set(actualVolume);
    }
  }, [volume]);

  const getVolumeIcon = () => {
    const volumeLevel = 1 - volume;

    if (volumeLevel === 0) {
      // Muted icon (speaker with X)
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    } else if (volumeLevel < 0.3) {
      // Low volume (speaker only, no sound waves)
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217z" clipRule="evenodd" />
        </svg>
      );
    } else if (volumeLevel < 0.7) {
      // Medium volume (speaker with 1 sound wave)
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM11.828 5.757a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
        </svg>
      );
    } else {
      // High volume (speaker with 2 sound waves)
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  return (
    <div
      className="relative flex flex-col items-center px-2 group"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => !isDragging && setIsHovering(false)}
    >
      {/* Volume Icon */}
      <div
        className="text-text-muted hover:text-text transition-colors cursor-pointer p-1"
        onClick={handleIconClick}
        onWheel={handleWheel}
      >
        {getVolumeIcon()}
      </div>

      {/* Volume Slider - Shows on hover or when dragging, absolutely positioned */}
      {/* Added padding wrapper to create comfortable hover zone without gaps */}
      <div
        className={`absolute bottom-full pb-2 flex flex-col items-center transition-all duration-200 ${
          isHovering || isDragging ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none translate-y-2'
        }`}
      >
        <div className="flex flex-col items-center px-4 pb-2">
          {/* Volume Percentage Display */}
          <div className="mb-3 px-2 py-1 bg-neutral-800 border border-neutral-700 rounded shadow-lg">
            <span className="text-[11px] text-text font-mono tabular-nums whitespace-nowrap">
              {Math.round((1 - volume) * 100)}%
            </span>
          </div>

          <div
            ref={trackRef}
            className={`relative w-3 bg-neutral-800 border border-neutral-900 rounded-full cursor-ns-resize hover:w-4 transition-all shadow-inner select-none ${
              isDragging ? 'w-4' : ''
            }`}
            style={{ height: `${HEIGHT}px` }}
            onMouseDown={handleMouseDown}
            onWheel={handleWheel}
          >
            {/* Volume Fill (from bottom to current level) */}
            <div
              className="absolute bottom-0 left-0 w-full bg-accent rounded-full transition-all duration-100 pointer-events-none"
              style={{ height: `${volumePercentage}%` }}
            />

            {/* Volume Handle/Thumb */}
            <div
              className={`absolute left-1/2 w-4 h-4 bg-white border-2 border-accent rounded-full shadow-lg transition-all duration-100 -translate-x-1/2 pointer-events-none ${
                isDragging ? 'scale-125' : 'hover:scale-110'
              }`}
              style={{ top: `${100 - volumePercentage}%`, marginTop: '-8px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Volume;
